import { useReducer } from 'react';

import { request } from 'core/services/bff/presenters';
import { getRequestId } from 'core/services/bff/client/get-request-id';

import type { AuthCredentials, AuthPayload } from 'core/domains/auth';
import type { RequestError } from 'core/services/utilities/request/request.d';
import { parseJwt } from '../helpers';
import { utilities } from 'core';
import { flushSync } from 'react-dom';

type CustomErrorLogin = RequestError & {
  userMessage?: string;
  code?: number;
  statusCode?: number;
};

type LoginStateType = {
  data: AuthPayload | undefined;
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
  error: CustomErrorLogin;
};

enum Action {
  setData = 'SET_DATA',
  setIsSuccess = 'SET_ISSUCCESS',
  setIsError = 'SET_ISERROR',
  setIsLoading = 'SET_ISLOADING',
  setError = 'SET_ERROR',
}

const loginState = {
  data: undefined,
  isSuccess: false,
  isError: false,
  isLoading: false,
  error: {} as CustomErrorLogin,
};

const loginReducer = utilities.createReducer<LoginStateType>(
  {
    [Action.setData]: (draft, { payload }) => {
      draft.data = payload;
    },
    [Action.setIsSuccess]: (draft, { payload }) => {
      draft.isSuccess = true;
      draft.isError = false;
      draft.isLoading = false;
    },
    [Action.setIsError]: (draft, { payload }) => {
      draft.isError = true;
      draft.isSuccess = false;
      draft.isLoading = false;
    },
    [Action.setIsLoading]: (draft, { payload }) => {
      draft.isLoading = true;
      draft.isError = false;
      draft.isSuccess = false;
    },
    [Action.setError]: (draft, { payload }) => {
      draft.isLoading = false;
      draft.isError = true;
      draft.isSuccess = false;
      draft.error = payload;
    },
  },
  loginState,
);

export const useLoginRequest = () => {
  const [state, dispatch] = useReducer(loginReducer, loginState);

  const getLogin = async (data: AuthCredentials) => {
    flushSync(() => {
      dispatch({ type: Action.setIsLoading, payload: '' });
    });

    return await request
      .login(data)
      .then(({ data: response }) => {
        const resellerId = response?.id_token || parseJwt(response?.access_token || '')?.altid;

        window?.newrelic?.setUserId(resellerId);

        window?.newrelic?.addPageAction('callback_login', {
          event: 'analytics-event',
          event_name: 'callback_login',
          cd_interaction_detail: 'sucesso',
          login: data?.username,
          enduser_id: resellerId,
          session_id: getRequestId(),
        });

        dispatch({ type: Action.setData, payload: response });
        dispatch({ type: Action.setIsSuccess, payload: '' });
      })
      .catch((error) => {
        const { response } = error;
        if (response?.data?.reason) {
          window?.newrelic?.addPageAction('recaptcha-error', {
            ...(response?.data || {}),
            criteria: response?.data?.criteria || '',
            session_id: getRequestId() || '',
            reason: response?.data?.reason || '',
          });
        } else if (response?.data?.userMessage || error?.detail) {
          window?.newrelic?.addPageAction('callback_error_login', {
            ...(response?.data || {}),
            errorDetail: error?.detail || '',
            responseCode: response?.code || '',
            statusCode: error?.status || '',
            username: data?.username || '',
            session_id: getRequestId() || '',
          });
        } else {
          window?.newrelic?.noticeError(error, {
            ...(response?.data || {}),
            errorDetail: error?.detail || '',
            responseCode: response?.code || '',
            statusCode: error?.status || '',
            username: data?.username || '',
            session_id: getRequestId() || '',
            step: 'callback_error_login_final',
          });
        }
        dispatch({ type: Action.setError, payload: response?.data || error });
        dispatch({ type: Action.setIsError, payload: '' });
      });
  };

  return {
    getLogin,
    ...state,
  };
};
